/**
 * Fort panel — v0M7.4d+garrison.3 (2026-07-27).
 *
 * Lightweight modal for one player-owned Fort. Shows garrison count +
 * siege state (if any) + a Recruit Garrison button. Reuses the sim
 * path shipped at v0M7.4d+garrison.1 (recruit_cohort with
 * worldStructureId targeting the Fort).
 *
 * Rebuild discipline: DOM structure built ONCE per fortId change;
 * `sync()` mutates existing element text/disabled states only.
 */

import { injectMenuStyles } from './menu-styles';
import { loadCohorts } from '../../data/cohorts';
import { loadDials, getDialNumber } from '../../data/dials';
import type { Command } from '../../sim/commands';
import type { LoadedMap } from '../map/map-loader';
import type {
  FactionId,
  GameState,
  ProvinceId,
  WorldStructure,
  WorldStructureId,
} from '../../sim/state';
import {
  TICKS_PER_GAME_DAY,
  toProvinceId,
} from '../../sim/state';

const FORT_PANEL_DIALS = loadDials();
const GARRISON_MAX = Math.max(0, Math.floor(getDialNumber(FORT_PANEL_DIALS, 'garrison_max_per_settlement')));
const COHORTS = loadCohorts();
const GARRISON_COHORT_TYPE = 'Garrison';

export interface FortPanelCallbacks {
  onIssue(cmd: Command): void;
  onClose(): void;
}

export interface FortPanelHandle {
  sync(state: GameState, map: LoadedMap, openFortId: WorldStructureId | null, playerFactionId: FactionId | null): void;
  destroy(): void;
}

export function mountFortPanel(
  root: HTMLElement,
  callbacks: FortPanelCallbacks,
): FortPanelHandle {
  injectMenuStyles();

  const backdrop = document.createElement('div');
  backdrop.className = 'su-city-panel-backdrop';
  backdrop.style.display = 'none';

  const panel = document.createElement('div');
  panel.className = 'su-city-panel';
  backdrop.appendChild(panel);

  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) callbacks.onClose();
  });

  // Header ---------------------------------------------------------
  const header = document.createElement('div');
  header.className = 'su-city-panel-header';
  const title = document.createElement('h2');
  title.className = 'su-city-panel-title';
  header.appendChild(title);
  const closeBtn = document.createElement('button');
  closeBtn.className = 'su-city-panel-close';
  closeBtn.textContent = '×';
  closeBtn.title = 'Close (Esc)';
  closeBtn.addEventListener('click', () => callbacks.onClose());
  header.appendChild(closeBtn);
  panel.appendChild(header);

  const body = document.createElement('div');
  body.className = 'su-city-panel-body';
  panel.appendChild(body);

  // Location line --------------------------------------------------
  const locationRow = document.createElement('div');
  locationRow.className = 'su-city-panel-note';
  locationRow.style.marginBottom = '6px';
  body.appendChild(locationRow);

  // Siege banner (only visible when besieged) ----------------------
  const siegeCard = document.createElement('div');
  siegeCard.style.padding = '8px 12px';
  siegeCard.style.borderRadius = '4px';
  siegeCard.style.marginBottom = '10px';
  siegeCard.style.background = 'rgba(200,80,60,0.18)';
  siegeCard.style.display = 'none';
  const siegeLabel = document.createElement('div');
  siegeLabel.style.fontWeight = 'bold';
  siegeLabel.style.color = '#d97070';
  siegeCard.appendChild(siegeLabel);
  body.appendChild(siegeCard);

  // Garrison row ---------------------------------------------------
  const garrisonRow = document.createElement('div');
  garrisonRow.className = 'su-city-panel-row';
  garrisonRow.style.marginBottom = '8px';
  const garrisonName = document.createElement('div');
  garrisonName.className = 'su-city-panel-item-name';
  garrisonName.textContent = 'Garrison';
  garrisonRow.appendChild(garrisonName);
  const garrisonCount = document.createElement('div');
  garrisonCount.className = 'su-city-panel-item-cost';
  garrisonCount.textContent = `0 / ${GARRISON_MAX}`;
  garrisonRow.appendChild(garrisonCount);
  const recruitBtn = document.createElement('button');
  recruitBtn.className = 'su-city-panel-action';
  recruitBtn.textContent = 'Recruit';
  garrisonRow.appendChild(recruitBtn);
  body.appendChild(garrisonRow);

  // Cost hint line -------------------------------------------------
  const costHint = document.createElement('div');
  costHint.className = 'su-city-panel-note';
  costHint.style.fontSize = '11px';
  costHint.style.opacity = '0.75';
  body.appendChild(costHint);

  // In-progress list -----------------------------------------------
  const queueHeader = document.createElement('div');
  queueHeader.className = 'su-city-panel-note';
  queueHeader.style.marginTop = '10px';
  queueHeader.style.fontWeight = 'bold';
  queueHeader.textContent = 'In Progress';
  queueHeader.style.display = 'none';
  body.appendChild(queueHeader);
  const queueList = document.createElement('div');
  body.appendChild(queueList);

  root.appendChild(backdrop);

  // Live state -----------------------------------------------------
  let renderedFortId: number | null = null;
  let currentFortId: WorldStructureId | null = null;
  let currentPlayerFactionId: FactionId | null = null;
  let currentProvinceId: ProvinceId | null = null;
  let lastQueueHash = '';
  interface QueueRow { container: HTMLElement; label: HTMLElement; fill: HTMLElement; recruitTimeSec: number; }
  let queueRows: QueueRow[] = [];

  recruitBtn.addEventListener('click', () => {
    if (currentFortId === null || currentPlayerFactionId === null || currentProvinceId === null) return;
    callbacks.onIssue({
      type: 'recruit_cohort',
      tick: 0,
      playerFactionId: currentPlayerFactionId,
      provinceId: currentProvinceId,
      cohortType: GARRISON_COHORT_TYPE,
      armyId: null,
      settlementId: null,
      worldStructureId: currentFortId,
    });
  });

  function structuralBuild(fort: WorldStructure): void {
    title.textContent = `Fort #${fort.id as unknown as number}`;
    // Reset the queue viz — will re-populate in refresh().
    queueList.innerHTML = '';
    queueRows = [];
    lastQueueHash = '';
  }

  function refresh(state: GameState, map: LoadedMap, fort: WorldStructure, playerFactionId: FactionId): void {
    const faction = state.factions.get(playerFactionId);
    if (!faction) return;

    // Resolve containing province from the map's provinceMap (Uint8Array).
    const idx = fort.cell.y * map.width + fort.cell.x;
    const pidRaw = idx >= 0 && idx < map.provinceMap.length ? map.provinceMap[idx] : 0;
    const pid = pidRaw === undefined ? 0 : pidRaw;
    currentProvinceId = pid > 0 ? toProvinceId(pid) : null;
    const province = currentProvinceId !== null ? state.provinces.get(currentProvinceId) : null;

    const provinceLabel = province ? `Province #${province.id as unknown as number}` : 'Wilderness';
    locationRow.textContent = `Location: cell (${fort.cell.x}, ${fort.cell.y}) — ${provinceLabel}`;

    // Siege banner.
    if (fort.siegeState != null) {
      siegeCard.style.display = '';
      siegeLabel.textContent = `UNDER SIEGE — surrenders in ${Math.max(0, fort.siegeState.daysRemaining).toFixed(1)} days`;
    } else {
      siegeCard.style.display = 'none';
    }

    // Garrison + recruit button.
    const currentCount = (fort.garrisonCohortIds ?? []).length;
    // Count queued recruits targeting this fort across all provinces.
    let queuedForFort = 0;
    for (const p of state.provinces.values()) {
      for (const o of p.recruitQueue) {
        if (o.worldStructureId === fort.id) queuedForFort++;
      }
    }
    const total = currentCount + queuedForFort;
    garrisonCount.textContent = `${currentCount} / ${GARRISON_MAX}` + (queuedForFort > 0 ? ` (+${queuedForFort} in queue)` : '');

    const spec = COHORTS[GARRISON_COHORT_TYPE];
    if (!spec || !province || province.ownerFactionId !== playerFactionId) {
      recruitBtn.disabled = true;
      recruitBtn.textContent = 'Recruit';
      costHint.textContent = province && province.ownerFactionId !== playerFactionId
        ? 'Fort sits in a province you do not own.'
        : (spec ? 'Cannot resolve province.' : 'Garrison cohort spec missing.');
    } else {
      const atCap = total >= GARRISON_MAX;
      // Recruit draws manpower from the province's capital.
      let recruitingCity: import('../../sim/state').Settlement | null = null;
      for (const c of state.settlements.values()) {
        if (c.provinceId === province.id && !c.isMinor) { recruitingCity = c; break; }
      }
      const cityManpower = recruitingCity?.manpower ?? 0;
      const affordable =
        faction.resources.gold >= spec.goldCost &&
        faction.resources.iron >= spec.ironCost &&
        faction.resources.wood >= spec.woodCost &&
        faction.resources.horses >= spec.horsesCost &&
        faction.resources.supplies >= spec.suppliesCost &&
        cityManpower >= spec.figureCount;

      if (atCap) {
        recruitBtn.disabled = true;
        recruitBtn.textContent = 'At Cap';
        costHint.textContent = `Fort garrison at cap (${GARRISON_MAX}).`;
      } else {
        recruitBtn.disabled = !affordable;
        recruitBtn.textContent = 'Recruit';
        const bits: string[] = [];
        if (spec.goldCost > 0) bits.push(`${spec.goldCost}G`);
        if (spec.ironCost > 0) bits.push(`${spec.ironCost}I`);
        if (spec.woodCost > 0) bits.push(`${spec.woodCost}W`);
        if (spec.horsesCost > 0) bits.push(`${spec.horsesCost}H`);
        if (spec.suppliesCost > 0) bits.push(`${spec.suppliesCost}F`);
        bits.push(`${spec.figureCount}mp (from Province Capital)`);
        costHint.textContent = `Cost: ${bits.join(' ')} — ${spec.recruitTimeSec}s training. Manpower: ${Math.floor(cityManpower)} available.`;
      }
    }

    // Queue viz — mirror settlement-panel pattern. Rebuild on shape change,
    // mutate progress bar in place otherwise.
    const flightOrders: { p: import('../../sim/state').Province; oIndex: number; recruitTimeSec: number; label: string }[] = [];
    for (const p of state.provinces.values()) {
      for (let i = 0; i < p.recruitQueue.length; i++) {
        const o = p.recruitQueue[i]!;
        if (o.worldStructureId === fort.id) {
          const cSpec = COHORTS[o.cohortType];
          if (cSpec) {
            flightOrders.push({
              p,
              oIndex: i,
              recruitTimeSec: cSpec.recruitTimeSec,
              label: o.cohortType,
            });
          }
        }
      }
    }
    const structureHash = flightOrders.map((f) => f.label).join('|');
    if (structureHash !== lastQueueHash) {
      queueList.innerHTML = '';
      queueRows = [];
      for (const f of flightOrders) {
        const rowDiv = document.createElement('div');
        rowDiv.style.margin = '4px 0';
        const labelDiv = document.createElement('div');
        labelDiv.style.fontSize = '11px';
        labelDiv.style.opacity = '0.85';
        labelDiv.style.marginBottom = '2px';
        rowDiv.appendChild(labelDiv);
        const barOuter = document.createElement('div');
        barOuter.style.height = '6px';
        barOuter.style.background = 'rgba(255, 255, 255, 0.12)';
        barOuter.style.borderRadius = '3px';
        barOuter.style.overflow = 'hidden';
        const barFill = document.createElement('div');
        barFill.style.height = '100%';
        barFill.style.background = '#f4d47a';
        barFill.style.width = '0%';
        barFill.style.transition = 'width 200ms linear';
        barOuter.appendChild(barFill);
        rowDiv.appendChild(barOuter);
        queueList.appendChild(rowDiv);
        queueRows.push({ container: rowDiv, label: labelDiv, fill: barFill, recruitTimeSec: f.recruitTimeSec });
      }
      lastQueueHash = structureHash;
    }
    queueHeader.style.display = flightOrders.length > 0 ? '' : 'none';
    for (let i = 0; i < queueRows.length && i < flightOrders.length; i++) {
      const row = queueRows[i]!;
      const f = flightOrders[i]!;
      const order = f.p.recruitQueue[f.oIndex]!;
      const total = row.recruitTimeSec > 0 ? row.recruitTimeSec : 1;
      const pct = Math.max(0, Math.min(100, (order.progressSec / total) * 100));
      row.fill.style.width = `${pct.toFixed(1)}%`;
      const remaining = Math.max(0, total - order.progressSec);
      row.label.textContent = `${f.label} → ${remaining.toFixed(0)}s remaining`;
    }
    void TICKS_PER_GAME_DAY; // reserved for future days-based bar variant
  }

  function sync(state: GameState, map: LoadedMap, openFortId: WorldStructureId | null, playerFactionId: FactionId | null): void {
    if (openFortId === null || playerFactionId === null) {
      backdrop.style.display = 'none';
      renderedFortId = null;
      currentFortId = null;
      currentPlayerFactionId = null;
      return;
    }
    const fort = state.worldStructures?.get(openFortId);
    if (!fort || fort.kind !== 'fort' || fort.ownerFactionId !== playerFactionId) {
      backdrop.style.display = 'none';
      renderedFortId = null;
      currentFortId = null;
      currentPlayerFactionId = null;
      return;
    }
    currentFortId = openFortId;
    currentPlayerFactionId = playerFactionId;
    const key = openFortId as unknown as number;
    if (key !== renderedFortId) {
      structuralBuild(fort);
      renderedFortId = key;
    }
    refresh(state, map, fort, playerFactionId);
    backdrop.style.display = 'flex';
  }

  function destroy(): void {
    backdrop.remove();
  }

  return { sync, destroy };
}
